import { useEffect, useRef, useState } from "react";
import { CheckIcon } from "./ui";

/**
 * Copies `text` to the clipboard and swaps its label to "Copied" with a check for a beat. Both
 * labels share one grid cell, so the width is the wider label and never shifts neighbours. The
 * status is announced to screen readers.
 */
export function CopyButton({
  text,
  children,
  copiedLabel = "Copied",
  className,
  style,
  ariaLabel,
  onCopied,
}: {
  text: string;
  children: React.ReactNode;
  copiedLabel?: string;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
  /** Called after the text has actually reached the clipboard. */
  onCopied?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  async function onClick() {
    try {
      await navigator.clipboard.writeText(text);
      onCopied?.();
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked (permissions/insecure context) — nothing to show */
    }
  }

  return (
    <button
      type="button"
      className={className}
      aria-label={ariaLabel}
      style={{ ...style, display: "grid", placeItems: "center", whiteSpace: "nowrap" }}
      onClick={onClick}
    >
      <span style={{ gridArea: "1 / 1", visibility: copied ? "hidden" : "visible" }}>
        {children}
      </span>
      <span
        aria-hidden={!copied}
        style={{
          gridArea: "1 / 1",
          visibility: copied ? "visible" : "hidden",
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
        }}
      >
        <CheckIcon size={13} strokeWidth={2.8} />
        {copiedLabel}
      </span>
      <span className="sr-only" role="status">
        {copied ? "Copied to clipboard" : ""}
      </span>
    </button>
  );
}
